import { useState, useCallback, useEffect } from 'react';

type EditMode = 'pan' | 'elements';

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.1;

export function usePanBackground(
  postId: string,
  backgroundOffsetX: number,
  backgroundOffsetY: number,
  backgroundScale: number,
  editMode: EditMode,
  onSaveOffset: (postId: string, offsetX: number, offsetY: number, scale: number) => void,
) {
  const [dragOffset, setDragOffset] = useState({ x: backgroundOffsetX || 0, y: backgroundOffsetY || 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [initialOffset, setInitialOffset] = useState({ x: backgroundOffsetX || 0, y: backgroundOffsetY || 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [bgScale, setBgScale] = useState(backgroundScale || 1.0);

  // Reset when post changes
  useEffect(() => {
    setDragOffset({ x: backgroundOffsetX || 0, y: backgroundOffsetY || 0 });
    setInitialOffset({ x: backgroundOffsetX || 0, y: backgroundOffsetY || 0 });
    setBgScale(backgroundScale || 1.0);
    setHasUnsavedChanges(false);
  }, [postId]);

  const handlePanStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (editMode !== 'pan') return;
    e.preventDefault();
    setIsDragging(true);

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setDragStart({ x: clientX, y: clientY });
    setInitialOffset({ ...dragOffset });
  }, [editMode, dragOffset]);

  const handlePanMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !dragStart || editMode !== 'pan') return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const sensitivity = 0.3;
    const deltaX = (clientX - dragStart.x) * sensitivity;
    const deltaY = (clientY - dragStart.y) * sensitivity;

    const newOffsetX = Math.max(-100, Math.min(100, initialOffset.x + deltaX));
    const newOffsetY = Math.max(-100, Math.min(100, initialOffset.y + deltaY));

    setDragOffset({ x: newOffsetX, y: newOffsetY });
    setHasUnsavedChanges(true);
  }, [isDragging, dragStart, initialOffset, editMode]);

  const handlePanEnd = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const handleSavePosition = useCallback(() => {
    onSaveOffset(postId, dragOffset.x, dragOffset.y, bgScale);
    setHasUnsavedChanges(false);
  }, [postId, dragOffset, bgScale, onSaveOffset]);

  const handleResetOffset = useCallback(() => {
    setDragOffset({ x: 0, y: 0 });
    setBgScale(1.0);
    setHasUnsavedChanges(true);
  }, []);

  // Background scale controls (scroll wheel + buttons)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setBgScale((prev) => {
      const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
      return Math.round(Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev + delta)) * 100) / 100;
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleScaleUp = useCallback(() => {
    setBgScale((prev) => Math.round(Math.min(MAX_SCALE, prev + SCALE_STEP) * 100) / 100);
    setHasUnsavedChanges(true);
  }, []);

  const handleScaleDown = useCallback(() => {
    setBgScale((prev) => Math.round(Math.max(MIN_SCALE, prev - SCALE_STEP) * 100) / 100);
    setHasUnsavedChanges(true);
  }, []);

  const handleScaleReset = useCallback(() => {
    setBgScale(1.0);
    setHasUnsavedChanges(true);
  }, []);

  // Global mouse up handler for pan
  useEffect(() => {
    if (!isDragging) return;
    const handleGlobalUp = () => handlePanEnd();
    window.addEventListener('mouseup', handleGlobalUp);
    window.addEventListener('touchend', handleGlobalUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('touchend', handleGlobalUp);
    };
  }, [isDragging, handlePanEnd]);

  return {
    dragOffset,
    isDragging,
    hasUnsavedChanges,
    bgScale,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
    handleSavePosition,
    handleResetOffset,
    handleWheel,
    handleScaleUp,
    handleScaleDown,
    handleScaleReset,
  };
}
